<template>
    <div>
        <div class="flex flex-col">
            <div
                v-for="field in defaultFields"
                :key="'field-' + field"
                class="field-wrapper"
            >
                <h2 v-if="field == 'first_name'"
                    class="text-body font-semibold pl-4 first-letter:uppercase mb-16">
                    {{ $t('jobs.form') }}
                </h2>
                <div class="field-wrapper-child flex items-center mb-12 pl-16 pr-8">
                    <div class="font-semibold first-letter:uppercase mr-auto">
                        {{ $t('interims.field_names.' + field) }}
                    </div>
                    <segmented-button
                        :id="field"
                        v-model="form.dynamic_form_interim.input_states[field]"
                        :options="getSegmentedButtonOptions(field)"
                        :class="isRequiredField(field) ? 'opacity-50' : ''"
                    />
                </div>
            </div>
        </div>

        <div>
            <h2 class="text-body font-semibold pl-4 first-letter:uppercase mt-24 mb-24">
                {{ $t('jobs.create.headers.extra_questions') }}
            </h2>
            <draggable
                v-model="form.dynamic_form_interim.questions"
                handle=".draggable"
                :item-key="(item, index) => `field-${index}`"
            >
                <template #item="{ element, index }">
                    <div class="field-wrapper">
                        <div class="field-wrapper-child flex items-center mb-12 pl-16 pr-8">
                            <div class="font-semibold text-body-sm first-letter:uppercase mr-auto truncate">
                                {{ element.question[locale] }}
                            </div>
                            <div class="flex items-center mr-16 pl-24">
                                <button class="mr-12" @click="startEditingField(index)">
                                    <svg-icon class="text-primary-80 hover:text-primary trans-ease" size="16" type="edit"/>
                                </button>
                                <button class="mr-12" @click="removeField(index)">
                                    <svg-icon class="text-primary-80 hover:text-primary trans-ease" size="16" type="delete"/>
                                </button>
                                <div class="draggable cursor-move">
                                    <svg-icon class="text-primary-80" size="16" type="drag-handle"/>
                                </div>
                            </div>
                            <div class="flex rounded-32 overflow-hidden border">
                                <label
                                    :class="[ form.dynamic_form_interim.questions[index].required ? 'bg-semantic-info text-white border-primary-20' : 'bg-white border-primary-20 text-primary-80 hover:text-black hover:bg-primary-20' ]"
                                    class="py-4 px-8 flex-none"
                                >
                                    <input
                                        v-model="form.dynamic_form_interim.questions[index].required"
                                        class="hidden"
                                        type="checkbox"
                                    >
                                    <span>
                                        {{ $t('jobs.input_state.required') }}
                                    </span>
                                </label>
                                <label
                                    :class="[ !form.dynamic_form_interim.questions[index].required ? 'bg-semantic-warning text-white border-primary-20' : 'bg-white border-primary-20 text-primary-80 hover:text-black hover:bg-primary-20']"
                                    class="py-4 px-8 flex-none"
                                >
                                    <input
                                        v-model="form.dynamic_form_interim.questions[index].required"
                                        class="hidden"
                                        type="checkbox"
                                    >
                                    <span>
                                        {{ $t('jobs.input_state.optional') }}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </template>
            </draggable>
        </div>

        <button
            :class="[ Object.keys(form.dynamic_form_interim.questions).length ? 'mt-24' : 'mt-8' ]"
            class="py-16 rounded border border-primary-20 bg-primary-5 w-full flex items-center justify-center group text-primary-20 hover:text-primary-80"
            @click="startAddingField"
        >
            <div class="flex items-center">
                <div class="trans-ease group-hover:bg-primary-80 w-32 h-32 bg-primary-20 rounded-32 flex items-center justify-center text-white mr-10">
                    <svg-icon class="w-16 h-16" type="add"></svg-icon>
                </div>
                <span class="text-body font-semibold first-letter:uppercase">{{ $t('jobs.add_extra_question') }}</span>
            </div>
        </button>

        <div class="pt-32">
            <div class="flex items-center justify-end gap-12">
                <simple-button
                    icon="keyboard-arrow-left"
                    icon-position="left"
                    look="text"
                    @click="$emit('step' ,1)"
                >
                    {{ $t('interims.back_to_selection') }}
                </simple-button>
                <simple-button
                    display="secondary"
                    icon="save"
                    :loading="form.submitting || loading"
                    look="primary"
                    @click="save">
                    {{ (confirmButton ? confirmButton : this.$t('jobs.save_and_next')) }}
                </simple-button>
            </div>
        </div>

        <form-field-editor v-if="addingField" :exclude_types="['file']" @close="stopAddingField" @input="addField"></form-field-editor>
        <form-field-editor v-if="editingField != null" :resource="editingFieldResource" @close="stopEditingField" @input="editField"></form-field-editor>

    </div>
</template>

<script>
import Form from '../../etc/forms/Form';
import Job from '../../models/Job';
import SvgIcon from './SvgIcon.vue';
import FormFieldEditor from './FormFieldEditor.vue';
import SimpleButton from './SimpleButton.vue';
import draggable from 'vuedraggable';
import * as jobs from '../../services/JobService';
import {normalizeJson} from '../../../shared/helpers';
import SegmentedButton from "./SegmentedButton.vue";
import Interim from "../../models/Interim";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useRepo} from "pinia-orm";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

function getFields() {
    return [
        {
            name: 'dynamic_form_interim',
            value: {
                'input_states': {
                    'first_name': 'required',
                    'last_name': 'required',
                    'email': 'required',
                    'cv': 'required',
                    'motivation': 'required',
                    'city': 'required',
                    'postal_code': 'hidden',
                    'salary_expectation': 'hidden',
                },
                questions: [],
            },
        },
    ];
}

export default {
    components: {
        SegmentedButton,
        'svg-icon': SvgIcon,
        'form-field-editor': FormFieldEditor,
        'simple-button': SimpleButton,
        'draggable': draggable,
    },
    props: {
        interim: Interim,
        job: Job,
        confirmButton: {
            required: false,
            type: String,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        selectingJobs: Boolean,
        selectedJobs: Array
    },
    data: function () {
        return {
            defaultFields: Object.keys(getFields()[0].value.input_states),
            form: this.generateForm(),
            addingField: false,
            editingField: null,
            segmentedButtonOptions: ['required', 'optional', 'hidden'],
            segmentedButtonSharedClasses: 'bg-white border-primary-20 text-primary-80',
            segmentedButtonHoverClasses: 'hover:text-black hover:bg-primary-20',
            segmentedButtonSelectedClasses: {
                required: 'bg-semantic-info text-white border-primary-20',
                optional: 'bg-semantic-warning text-white border-primary-20',
                hidden: 'bg-semantic-error text-white border-primary-20',
            }
        };
    },
    computed: {
        ...mapStores(useMainStore),
        locale() {
            return this.mainStore.locale;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        editingFieldResource() {
            if (this.editingField == null) {
                return null;
            }

            return this.form.dynamic_form_interim.questions[this.editingField];
        },
        hasDynamicQuestions() {
            return this.form.dynamic_form_interim.questions.length > 0;
        },
    },
    methods: {
        generateForm() {
            const job = this.job
                || useRepo(Job)
                    .find(this.interim.interimJobs?.find(interimJob =>
                        interimJob.closed_at === null && !!interimJob.published)?.job_id
                    )
                || useRepo(Job).find(this.selectedJobs[0]);

            let fields = getFields();
            let extraFields = {};

            // always add the required fields
            this.getRequiredFields().forEach(
                field => extraFields[field] = 'required',
            );

            // always add some hidden fields for backward compatibility
            this.getHiddenFields().forEach(
                field => extraFields[field] = 'hidden',
            );

            fields[0].value.input_states = {
                ...fields[0].value.input_states,
                ...job.dynamic_form_interim.input_states,
                ...extraFields,
            };

            fields[0].value.questions = [...job.dynamic_form_interim.questions];

            return new Form(fields);
        },
        getHiddenFields() {
            // return ['facebook', 'twitter', 'instagram'];
            return [];
        },
        getRequiredFields() {
            return ['first_name', 'last_name', 'email', 'cv', 'motivation'];
        },
        isRequiredField(field) {
            return this.getRequiredFields().includes(field);
        },
        startAddingField() {
            this.addingField = true;
        },
        stopAddingField() {
            this.addingField = false;
        },
        addField({data, success, error}) {

            let {question_type, question, disqualify_answer_no, required_answer_yes, options, multiple, required, key} = data;

            let field = {question_type, question, required, key};

            if (['free_text', 'multiple_choice'].includes(question_type)) {
                field['multiple'] = multiple;
            }

            if (question_type === 'multiple_choice') {
                field['options'] = options;
            }

            if (question_type === 'yes_no') {
                field['disqualify_answer_no'] = disqualify_answer_no;

                field['required_answer_yes'] = required_answer_yes;
            }

            this.$set(this.form.dynamic_form_interim, 'questions', [...this.form.dynamic_form_interim.questions, field]);

            return success();
        },
        startEditingField(fieldIndex) {
            this.editingField = fieldIndex;
        },
        stopEditingField() {
            this.editingField = null;
        },
        editField({data, success, error}) {

            let editedQuestions = [...this.form.dynamic_form_interim.questions];
            editedQuestions[this.editingField] = data;
            this.$set(this.form.dynamic_form_interim, 'questions', editedQuestions);

            return success();
        },
        removeField(index) {
            this.form.dynamic_form_interim.questions.splice(index, 1);
        },
        save() {
            if (this.selectingJobs)
                this.saveJobs();
            else
                this.saveJob();
        },
        saveJob() {
            this.form.submit(data => {
                return jobs.update(this.job.id, {
                    data: {
                        attributes: data,
                    },
                });
            }).then(result => {
                // update
                useRepo(Job).save(normalizeJson(result));

                //Go to next step
                this.$emit('step', 3);
            });
        },
        async saveJobs() {
            await this.form.submit(data => {
                return jobs.bulkDynamicFormFields(this.interim.id, this.selectedJobs, {
                    data: {
                        attributes: data,
                    },
                });
            }).then(results => {
                useRepo(Job).save(normalizeJson(results));
            }).then(() => this.$emit('step', 3));
        },
        getSegmentedButtonOptions(field) {
            let data = [];
            this.segmentedButtonOptions.forEach(value => {
                data.push({
                    label: this.$t('jobs.input_state.' + value),
                    value: value,
                    disabled: this.isRequiredField(field),
                    classes: (this.form.dynamic_form_interim.input_states[field] === value ?
                            this.segmentedButtonSelectedClasses[value] :
                            this.segmentedButtonSharedClasses + ' ' + (!this.isRequiredField(field) ? this.segmentedButtonHoverClasses : '')
                    )
                })
            });
            return data;
        }
    },
};
</script>

<style scoped>
.field-wrapper:nth-of-type(odd) .field-wrapper-child {
    @apply rounded py-8 bg-primary-5;
}
</style>
